import { useMemo, useState } from "react";
import {
  Box,
  Paper,
  TextField,
  Button,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Divider,
  Typography,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PageHeader from "../../components/common/PageHeader";
import FilterBar from "../../components/common/FilterBar";
import EmptyState from "../../components/common/EmptyState";
import { usePersistedListFilters } from "../../hooks/usePersistedListFilters";

import { useProviderSearch, useClientSearch } from "../../hooks/useMasterdataSearch";
import AsyncAutocompleteWithQuickCreate from "../../components/common/AsyncAutocompleteWithQuickCreate";
import type { Provider, Client } from "../../features/masterdata/types";
import {
  useExportProviderSummaryXlsxMutation,
  useLazyProviderSummaryQuery,
  useExportClientSummaryXlsxMutation,
  useLazyClientSummaryQuery,
} from "../../features/analytics/analyticsApi";

import { getTodayString } from "../../utils/date";

type Scope = "completed" | "all";

const defaultFilters = {
  dateFrom: getTodayString(),
  dateTo: getTodayString(),
  provider: "",
  client: "",
  scope: "completed" as Scope,
};

export default function AnalyticsPage() {
  const [filters, setFilters] = usePersistedListFilters("analytics", defaultFilters);
  const { dateFrom, dateTo, provider, client, scope } = filters;

  const [isProviderOpen, setProviderOpen] = useState(false);
  const [isClientOpen, setClientOpen] = useState(false);
  const providerSearch = useProviderSearch(provider || null, { menuOpen: isProviderOpen });
  const clientSearch = useClientSearch(client || null, { menuOpen: isClientOpen });
  const providerOptions = useMemo(
    () => [{ id: "", name: "All providers" } as Provider, ...providerSearch.options],
    [providerSearch.options]
  );
  const clientOptions = useMemo(
    () => [{ id: "", name: "All clients" } as Client, ...clientSearch.options],
    [clientSearch.options]
  );
  const isLoadingProviders = providerSearch.loading;
  const isLoadingClients = clientSearch.loading;

  const [runSummary, { data, isFetching, error }] = useLazyProviderSummaryQuery();
  const [runClientSummary, { data: clientData, isFetching: isClientFetching, error: clientError }] =
    useLazyClientSummaryQuery();

  const rows = data?.results ?? [];
  const clientRows = clientData?.results ?? [];

  const totals = useMemo(() => {
    return {
      transfers: data?.totals?.transfers ?? 0,
      sum_price_to_provider: data?.totals?.sum_price_to_provider ?? "0.00",
      sum_price_to_client: data?.totals?.sum_price_to_client ?? "0.00",
    };
  }, [data]);

  const clientTotals = useMemo(() => {
    return {
      transfers: clientData?.totals?.transfers ?? 0,
      sum_price_to_provider: clientData?.totals?.sum_price_to_provider ?? "0.00",
      sum_price_to_client: clientData?.totals?.sum_price_to_client ?? "0.00",
    };
  }, [clientData]);

  const onRun = async () => {
    if (!dateFrom || !dateTo) return;
    await runSummary({
      date_from: dateFrom,
      date_to: dateTo,
      provider: provider || "",
      include_all_statuses: scope === "all",
    });
  };

  const onRunClient = async () => {
    if (!dateFrom || !dateTo) return;
    await runClientSummary({
      date_from: dateFrom,
      date_to: dateTo,
      client: client || "",
      include_all_statuses: scope === "all",
    });
  };

  const [exportXlsx, { isLoading: isExporting }] = useExportProviderSummaryXlsxMutation();
  const [exportClientXlsx, { isLoading: isExportingClient }] = useExportClientSummaryXlsxMutation();

  const showNeedDates = !dateFrom || !dateTo;
  const showNoResults = !showNeedDates && !isFetching && !error && data && rows.length === 0;
  const showNoClientResults =
    !showNeedDates && !isClientFetching && !clientError && clientData && clientRows.length === 0;

  return (
    <Box>
      <PageHeader
        title="Analytics"
        subtitle="Provider and client billing-style summaries"
      />

      <FilterBar
        label="Report parameters (date range and scope apply to both reports)"
        loading={isFetching || isClientFetching || isLoadingProviders || isLoadingClients}
      >
        <TextField
          label="Date from"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={dateFrom}
          onChange={(e) => setFilters({ dateFrom: e.target.value })}
          size="small"
          sx={{ minWidth: { xs: 0, sm: 160 }, width: { xs: "100%", sm: "auto" } }}
        />
        <TextField
          label="Date to"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={dateTo}
          onChange={(e) => setFilters({ dateTo: e.target.value })}
          size="small"
          sx={{ minWidth: { xs: 0, sm: 160 }, width: { xs: "100%", sm: "auto" } }}
        />
        <TextField
          label="Status scope"
          select
          value={scope}
          onChange={(e) => setFilters({ scope: e.target.value as Scope })}
          size="small"
          sx={{ minWidth: { xs: 0, sm: 180 }, width: { xs: "100%", sm: "auto" } }}
        >
          <MenuItem value="completed">Completed only</MenuItem>
          <MenuItem value="all">All statuses</MenuItem>
        </TextField>
      </FilterBar>

      {error ? (
        <Typography sx={{ mt: 1 }} color="error">
          Failed to load provider analytics.
        </Typography>
      ) : null}

      {/* Provider Summary */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Provider Summary
        </Typography>
        <FilterBar label="" loading={false}>
          <Box sx={{ minWidth: { xs: 0, sm: 200 }, width: { xs: "100%", sm: "auto" } }}>
            <AsyncAutocompleteWithQuickCreate<Provider>
              label="Provider filter"
              value={provider}
              onChange={(id) => setFilters({ provider: id ?? "" })}
              options={providerOptions}
              getOptionLabel={(p) => p.name}
              showCreate={false}
              loading={providerSearch.loading}
              onInputChange={(_, v) => providerSearch.setSearchTerm(v)}
              filterOptions={(opts) => opts}
              onOpen={() => setProviderOpen(true)}
              onClose={() => setProviderOpen(false)}
              placeholder="All providers"
            />
          </Box>
          <Button variant="contained" onClick={onRun} disabled={showNeedDates || isFetching}>
            {isFetching ? "Running…" : "Run"}
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDownloadOutlinedIcon />}
            onClick={() =>
              exportXlsx({
                date_from: dateFrom,
                date_to: dateTo,
                provider: provider || undefined,
                include_all_statuses: scope === "all",
              })
            }
            disabled={!dateFrom || !dateTo || isExporting}
          >
            {isExporting ? "Exporting…" : "Export"}
          </Button>
        </FilterBar>
        {showNeedDates ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Set date range above, then click Run.
          </Typography>
        ) : !data ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Click Run to generate provider summary.
          </Typography>
        ) : showNoResults ? (
          <EmptyState title="No results" description="No transfers found for the selected range and provider filter." />
        ) : (
          <Paper variant="outlined" sx={{ mt: 1 }}>
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Transfers: {totals.transfers} • Sum cost (provider): €{totals.sum_price_to_provider} • Sum charge
                (client): €{totals.sum_price_to_client}
              </Typography>
            </Box>
            <Divider />
            <Box sx={{ overflow: "auto" }}>
              <Table sx={{ minWidth: 400 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Provider</TableCell>
                    <TableCell align="right">Transfers</TableCell>
                    <TableCell align="right">Sum cost (provider)</TableCell>
                    <TableCell align="right">Sum charge (client)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r: any) => (
                    <TableRow key={r.provider_id} hover>
                      <TableCell>{r.provider_name}</TableCell>
                      <TableCell align="right">{r.transfers}</TableCell>
                      <TableCell align="right">€{r.sum_price_to_provider}</TableCell>
                      <TableCell align="right">€{r.sum_price_to_client}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Paper>
        )}
      </Box>

      {/* Client Summary */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Client Summary
        </Typography>
        <FilterBar label="" loading={false}>
          <Box sx={{ minWidth: { xs: 0, sm: 200 }, width: { xs: "100%", sm: "auto" } }}>
            <AsyncAutocompleteWithQuickCreate<Client>
              label="Client filter"
              value={client}
              onChange={(id) => setFilters({ client: id ?? "" })}
              options={clientOptions}
              getOptionLabel={(c) => c.name}
              showCreate={false}
              loading={clientSearch.loading}
              onInputChange={(_, v) => clientSearch.setSearchTerm(v)}
              filterOptions={(opts) => opts}
              onOpen={() => setClientOpen(true)}
              onClose={() => setClientOpen(false)}
              placeholder="All clients"
            />
          </Box>
          <Button variant="contained" onClick={onRunClient} disabled={showNeedDates || isClientFetching}>
            {isClientFetching ? "Running…" : "Run"}
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDownloadOutlinedIcon />}
            onClick={() =>
              exportClientXlsx({
                date_from: dateFrom,
                date_to: dateTo,
                client: client || undefined,
                include_all_statuses: scope === "all",
              })
            }
            disabled={!dateFrom || !dateTo || isExportingClient}
          >
            {isExportingClient ? "Exporting…" : "Export"}
          </Button>
        </FilterBar>
        {clientError ? (
          <Typography sx={{ mt: 1 }} color="error">
            Failed to load client analytics.
          </Typography>
        ) : null}
        {showNeedDates ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Set date range above, then click Run.
          </Typography>
        ) : !clientData ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Click Run to generate client summary.
          </Typography>
        ) : showNoClientResults ? (
          <EmptyState
            title="No results"
            description="No transfers found for the selected range and client filter."
          />
        ) : (
          <Paper variant="outlined" sx={{ mt: 1 }}>
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Transfers: {clientTotals.transfers} • Sum cost (provider): €{clientTotals.sum_price_to_provider} • Sum
                charge (client): €{clientTotals.sum_price_to_client}
              </Typography>
            </Box>
            <Divider />
            <Box sx={{ overflow: "auto" }}>
              <Table sx={{ minWidth: 400 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Client</TableCell>
                    <TableCell align="right">Transfers</TableCell>
                    <TableCell align="right">Sum cost (provider)</TableCell>
                    <TableCell align="right">Sum charge (client)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {clientRows.map((r: any, idx: number) => (
                    <TableRow key={r.client_id ?? `no-client-${idx}`} hover>
                      <TableCell>{r.client_name}</TableCell>
                      <TableCell align="right">{r.transfers}</TableCell>
                      <TableCell align="right">€{r.sum_price_to_provider}</TableCell>
                      <TableCell align="right">€{r.sum_price_to_client}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Paper>
        )}
      </Box>
    </Box>
  );
}
